/**
 * Login Modal for @ollaid/native-sso
 * Complete login flow aligned with Native SSO design
 * 
 * @version 2.7.9
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Button, Input, Label,
  IconShieldCheck, IconMail, IconPhone, IconLock, IconKeyRound, IconLink,
  IconArrowLeft, IconEye, IconEyeOff, IconCheckCircle2, IconLoader2,
  BrandLinkRow,
} from './ui';
import OTPInput from './OTPInput';
import PasswordRecoveryModal from './PasswordRecoveryModal';
import { useNativeAuth } from '../hooks/useNativeAuth';
import { getAuthToken } from '../services/api';
import type { UserInfos } from '../types/native';
import packageFavicon from '../logo/favicon.png';

type LoginStep = 'choice' | 'email-check' | 'email-password' | 'email-otp' | 'email-create-password' | 'phone-input' | 'phone-otp' | 'access-otp';

export interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSwitchToSignup: () => void;
  onLoginSuccess?: (token: string, user: UserInfos) => void;
  saasApiUrl: string;
  iamApiUrl: string;
  logoUrl?: string;
  loading?: boolean;
  showSwitchToSignup?: boolean;
  homeUrl?: string;
  /** Type de compte par défaut à persister dans le storage du package */
  defaultAccountType?: 'user' | 'client';
  /** Pre-fill phone number and go directly to phone-input step */
  initialPhone?: string;
}

type LoginResult = {
  success: boolean;
  user?: Partial<UserInfos> & Pick<UserInfos, 'name'>;
  status?: string;
  callback_token?: string;
};

const SSO_VERSION = '2.8.2';

// Design tokens
const C = {
  primary: '#002147',
  accent: '#e8430a',
  green: '#16a34a',
  greenBg: '#dcfce7',
  amber: '#f59e0b',
  amberBg: '#fef3c7',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray500: '#6b7280',
  gray700: '#374151',
  gray900: '#111827',
  red: '#dc2626',
  redBg: '#fef2f2',
  white: '#ffffff',
} as const;

// Reusable style helpers
const iconCircle = (bg: string): React.CSSProperties => ({
  margin: '0 auto', width: '3rem', height: '3rem', backgroundColor: bg, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const appBadgeCircle = (bg: string): React.CSSProperties => ({
  width: '2.9rem',
  height: '2.9rem',
  borderRadius: '9999px',
  backgroundColor: bg,
  border: `1px solid ${C.gray200}`,
  boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5rem',
  boxSizing: 'border-box',
  overflow: 'hidden',
  flexShrink: 0,
});

const brandLinkPill: React.CSSProperties = {
  width: '2.2rem',
  height: '2.2rem',
  borderRadius: '9999px',
  backgroundColor: C.gray100,
  border: `1px solid ${C.gray200}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
};

const brandRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.65rem',
  marginBottom: '0.9rem',
};

const backBtnStyle: React.CSSProperties = {
  position: 'absolute', left: '1rem', top: '1rem', background: 'none', border: 'none',
  cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem', color: C.gray700, zIndex: 10,
};

const linkStyle: React.CSSProperties = {
  color: C.primary, cursor: 'pointer', background: 'none', border: 'none',
  fontWeight: 500, textDecoration: 'underline', fontSize: '0.875rem',
};

const errorBoxStyle: React.CSSProperties = {
  padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: C.redBg,
  color: C.red, fontSize: '0.875rem',
};

const methodBtnStyle: React.CSSProperties = {
  width: '100%', height: '3.5rem', justifyContent: 'flex-start', gap: '0.75rem',
  textAlign: 'left' as const, backgroundColor: C.gray100, borderColor: C.gray200,
};

const methodIconStyle = (bg: string): React.CSSProperties => ({
  width: '2.5rem', height: '2.5rem', backgroundColor: bg, borderRadius: '0.5rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});

export function LoginModal({
  open, onOpenChange, onSwitchToSignup, onLoginSuccess, saasApiUrl, iamApiUrl, logoUrl, loading, showSwitchToSignup = true, homeUrl, defaultAccountType, initialPhone,
}: LoginModalProps) {
  const {
    status, user, application, alternativeMethod, loading: authLoading, error: authError,
    loginWithEmail, loginWithPhone, submitPassword, submitOtp, grantAccess, loginWithAccessOtp,
    resendOtp, reset: resetAuth, clearError, otpMethod, otpSentTo,
  } = useNativeAuth({ saasApiUrl, iamApiUrl, defaultAccountType });

  const [step, setStep] = useState<LoginStep>('choice');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [accessOtpCode, setAccessOtpCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginData, setLoginData] = useState<{ token: string; user: UserInfos } | null>(null);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const successCallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const CCPHONE = '+221';
  const isSubmitting = authLoading || loading;
  const error = localError || authError;
  const applicationLogo = application?.logo || logoUrl || null;
  const applicationName = application?.name || 'Application';
  const showHomeLink = Boolean(homeUrl);
  const getOtpDeliveryLabel = (method?: 'email' | 'sms' | null, sentTo?: string | null, fallback?: 'email' | 'phone') => {
    if (method === 'sms') {
      return sentTo ? `par SMS au ${sentTo}` : 'par SMS';
    }
    if (method === 'email') {
      return sentTo ? `par email à ${sentTo}` : 'par email';
    }

    if (fallback === 'phone') {
      return phone ? `par SMS au ${CCPHONE} ${phone}` : 'par SMS';
    }

    return email ? `par email à ${email}` : 'pour vérification';
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(p => p - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (status === 'pending_password') setStep('email-password');
    else if (status === 'pending_otp') {
      if (step === 'email-check' || step === 'email-password') setStep('email-otp');
      else if (step === 'phone-input') setStep('phone-otp');
      setResendCooldown(60);
    }
  }, [status, step]);

  const resetState = useCallback(() => {
    if (successCallbackTimerRef.current) {
      clearTimeout(successCallbackTimerRef.current);
      successCallbackTimerRef.current = null;
    }
    setStep('choice'); setEmail(''); setPassword('');
    setShowPassword(false); setUserName(null); setPhone(''); setPhoneOtpCode(''); setEmailOtpCode('');
    setAccessOtpCode(''); setLocalError(null); setLoginSuccess(false); setLoginData(null);
    setResendCooldown(0); setPasswordSubmitting(false); resetAuth();
  }, [resetAuth]);

  useEffect(() => { if (!open) resetState(); }, [open, resetState]);

  // Pre-fill phone from conflict resolution
  useEffect(() => {
    if (open && initialPhone) {
      setStep('phone-input');
      setPhone(initialPhone);
    }
  }, [open, initialPhone]);

  /**
   * Finalise la connexion après que useNativeAuth ait déjà fait l'exchange.
   * Ne PAS re-appeler nativeAuthService.exchange() ici — le hook l'a déjà fait.
   */
  const finalizeLogin = (result: LoginResult) => {
    if (result.success && (result.callback_token || result.status === 'completed')) {
      const token = getAuthToken() || '';
      const resolvedUser = result.user || user;

      if (resolvedUser) {
        setLoginData({ token, user: resolvedUser as UserInfos });
        setLoginSuccess(true);
      }
      return;
    }

    if (result.status === 'needs_access') {
      setLoginSuccess(false);
      setLoginData(null);
    }
  };

  useEffect(() => {
    if (!loginSuccess || !loginData || !onLoginSuccess) return;

    if (successCallbackTimerRef.current) {
      clearTimeout(successCallbackTimerRef.current);
    }

    successCallbackTimerRef.current = setTimeout(() => {
      try {
        onLoginSuccess(loginData.token, loginData.user);
      } catch (callbackError) {
        console.error('Erreur dans onLoginSuccess', callbackError);
      }
    }, 900);

    return () => {
      if (successCallbackTimerRef.current) {
        clearTimeout(successCallbackTimerRef.current);
        successCallbackTimerRef.current = null;
      }
    };
  }, [loginSuccess, loginData, onLoginSuccess]);

  const handleEmailCheck = async () => {
    setLocalError(null); clearError();
    if (!email.trim()) { setLocalError("L'adresse email est requise"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setLocalError('Adresse email invalide'); return; }
    const result = await loginWithEmail(email.trim());
    if (result.success && user?.name) setUserName(user.name);
  };

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordSubmitting || isSubmitting) return;

    setPasswordSubmitting(true);
    setLocalError(null); clearError();
    try {
      if (!password) { setLocalError('Le mot de passe est requis'); return; }
      if (password.length < 8) { setLocalError('Le mot de passe doit contenir au moins 8 caractères'); return; }
      const result = await submitPassword(password);
      finalizeLogin(result);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleEmailOtpVerify = async () => {
    setLocalError(null); clearError();
    if (emailOtpCode.length !== 6) { setLocalError('Le code doit contenir 6 chiffres'); return; }
    const result = await submitOtp(emailOtpCode);
    finalizeLogin(result);
  };

  const handlePhoneInit = async () => {
    setLocalError(null); clearError();
    const cleanPhone = phone.replace(/\s/g, '');
    if (cleanPhone.length !== 9) { setLocalError('Le numéro doit contenir exactement 9 chiffres'); return; }
    await loginWithPhone(CCPHONE, cleanPhone);
  };

  const handlePhoneVerify = async () => {
    setLocalError(null); clearError();
    if (phoneOtpCode.length !== 6) { setLocalError('Veuillez entrer le code à 6 chiffres'); return; }
    const result = await submitOtp(phoneOtpCode);
    finalizeLogin(result);
  };

  const handleAccessOtpSubmit = async () => {
    setLocalError(null); clearError();
    if (accessOtpCode.length !== 8) { setLocalError('Veuillez entrer le code à 8 chiffres'); return; }
    const result = await loginWithAccessOtp(accessOtpCode);
    finalizeLogin(result);
  };

  const handleGrantAccess = async () => {
    setLocalError(null); clearError();
    const result = await grantAccess();
    finalizeLogin(result);
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    const result = await resendOtp();
    if (result.success) setResendCooldown(result.cooldown || 60);
  };

  const goBack = () => {
    setLocalError(null); clearError();
    switch (step) {
      case 'email-check': case 'phone-input': case 'access-otp': setStep('choice'); resetAuth(); break;
      case 'email-password': case 'email-otp': setStep('email-check'); resetAuth(); break;
      case 'phone-otp': setStep('phone-input'); resetAuth(); break;
      default: setStep('choice'); resetAuth();
    }
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && status === 'needs_access') {
      return;
    }

    onOpenChange(nextOpen);
  };

  const handleNeedsAccessCancel = () => {
    setLocalError(null);
    clearError();
    resetAuth();
    setStep('choice');
    onOpenChange(false);
  };

  const showNeedsAccess = status === 'needs_access' && Boolean(user && application);
  const showSuccess = loginSuccess && Boolean(loginData) && !showNeedsAccess;

  // Render helpers
  const renderError = () => error ? <div style={errorBoxStyle}>{error}</div> : null;

  const renderBackBtn = () => (
    <button onClick={goBack} style={backBtnStyle} type="button">
      <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
    </button>
  );

  const renderResendLink = () => (
    <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
      {resendCooldown > 0 ? (
        <span style={{ color: C.gray500 }}>Renvoyer dans {resendCooldown}s</span>
      ) : (
        <button type="button" style={linkStyle} onClick={handleResendOTP}>
          Code non reçu ? Renvoyer
        </button>
      )}
    </div>
  );

  const renderLoaderBtn = (text: string, loadingText: string, onClick: () => void, disabled: boolean, extraStyle?: React.CSSProperties) => (
    <Button onClick={onClick} disabled={isSubmitting || disabled} style={{ width: '100%', ...extraStyle }}>
      {isSubmitting ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconLoader2 style={{ width: '1rem', height: '1rem' }} />
          {loadingText}
        </span>
      ) : text}
    </Button>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent>

          {/* ===================== SUCCESS ===================== */}
          {showNeedsAccess ? (
            <>
              <DialogHeader style={{ textAlign: 'center' }}>
                <div style={{ ...iconCircle(C.primary + '1a'), width: '4rem', height: '4rem' }}>
                  {application?.logo ? (
                    <img src={application.logo} alt={application.name || 'Application'} style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.5rem' }} />
                  ) : (
                    <IconShieldCheck style={{ width: '2rem', height: '2rem', color: C.primary }} />
                  )}
                </div>
                <DialogTitle>Bienvenue sur {application?.name}</DialogTitle>
                <DialogDescription>
                  Bonjour <strong style={{ color: C.gray900 }}>{user?.name}</strong>, vous n'avez pas encore de compte sur <strong style={{ color: C.primary }}>{application?.name}</strong>.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div style={{ padding: '1rem', backgroundColor: C.gray100, borderRadius: '0.5rem', fontSize: '0.875rem', color: C.gray500 }}>
                  Votre compte <strong style={{ color: C.primary }}>iam.ollaid.com</strong> existe déjà. Cliquez sur Confirmer pour créer votre accès à <strong style={{ color: C.gray900 }}>{application?.name}</strong>.
                </div>
                {renderError()}
              </DialogBody>
              <DialogFooter>
                <Button onClick={handleGrantAccess} disabled={isSubmitting} style={{ width: '100%' }}>
                  {isSubmitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <IconLoader2 style={{ width: '1rem', height: '1rem' }} />
                      Création en cours...
                    </span>
                  ) : 'Confirmer la création de mon compte'}
                </Button>
                <Button variant="outline" onClick={() => { resetAuth(); setStep('choice'); onOpenChange(false); }} disabled={isSubmitting} style={{ width: '100%' }}>
                  Annuler
                </Button>
              </DialogFooter>
            </>
          ) : showSuccess ? (
            <DialogHeader style={{ textAlign: 'center' }}>
              <div style={{ ...iconCircle(C.greenBg), width: '5rem', height: '5rem' }}>
                <IconCheckCircle2 style={{ width: '3rem', height: '3rem', color: C.green }} />
              </div>
              <DialogTitle>Connexion réussie</DialogTitle>
              <DialogDescription>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <IconLoader2 style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
                  Redirection en cours...
                </span>
              </DialogDescription>
            </DialogHeader>

          /* ===================== ALIAS DISABLED ===================== */
          ) : alternativeMethod ? (
            <>
              <DialogHeader style={{ textAlign: 'center' }}>
                <div style={{ ...iconCircle(C.amberBg), width: '4rem', height: '4rem' }}>
                  {alternativeMethod.type === 'email' ? (
                    <IconMail style={{ width: '2rem', height: '2rem', color: C.amber }} />
                  ) : (
                    <IconPhone style={{ width: '2rem', height: '2rem', color: C.amber }} />
                  )}
                </div>
                <DialogTitle>Moyen de connexion désactivé</DialogTitle>
                <DialogDescription>
                  Vos identifiants sont corrects, mais vous avez désactivé ce moyen de connexion depuis votre compte IAM.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                {alternativeMethod.value && (
                  <div style={{ padding: '1rem', backgroundColor: C.gray100, borderRadius: '0.5rem' }}>
                    <p style={{ fontSize: '0.875rem', color: C.gray500, marginBottom: '0.75rem' }}>Vous pouvez vous connecter avec :</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: C.gray900 }}>
                      {alternativeMethod.type === 'phone' ? (
                        <IconPhone style={{ width: '1rem', height: '1rem', color: C.primary }} />
                      ) : (
                        <IconMail style={{ width: '1rem', height: '1rem', color: C.primary }} />
                      )}
                      <span>{alternativeMethod.value}</span>
                    </div>
                  </div>
                )}
              </DialogBody>
              <DialogFooter>
                <Button onClick={() => {
                  if (alternativeMethod.type === 'phone') setStep('phone-input');
                  else setStep('email-check');
                  resetAuth();
                }} style={{ width: '100%' }}>
                  {alternativeMethod.type === 'phone' ? 'Se connecter par téléphone' : 'Se connecter par email'}
                </Button>
                <Button variant="outline" onClick={() => window.open('https://iam.ollaid.com', '_blank')} style={{ width: '100%' }}>
                  Réactiver depuis iam.ollaid.com
                </Button>
                <Button variant="ghost" onClick={handleNeedsAccessCancel} style={{ width: '100%' }}>
                  Annuler
                </Button>
              </DialogFooter>
            </>

          /* ===================== MAIN STEPS ===================== */
          ) : (
            <>
              {step !== 'choice' && renderBackBtn()}

              {/* ===== CHOICE ===== */}
              {step === 'choice' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <div style={brandRowStyle}>
                      <div style={appBadgeCircle(C.white)}>
                        {applicationLogo ? (
                          <img
                            src={applicationLogo}
                            alt={applicationName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <IconShieldCheck style={{ width: '1.4rem', height: '1.4rem', color: C.primary }} />
                        )}
                      </div>
                      <div style={brandLinkPill} aria-hidden="true">
                        <IconLink style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
                      </div>
                      <div style={appBadgeCircle(C.white)}>
                        <img
                          src={packageFavicon}
                          alt="Ollaid SSO"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    </div>
                    <DialogTitle>Connexion</DialogTitle>
                    <DialogDescription>Choisissez votre méthode de connexion</DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {renderError()}
                      <Button variant="outline" onClick={() => setStep('email-check')} style={methodBtnStyle}>
                        <div style={methodIconStyle(C.accent)}>
                          <IconMail style={{ width: '1.25rem', height: '1.25rem', color: C.white }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: C.gray900 }}>Adresse email</div>
                          <div style={{ fontSize: '0.75rem', color: C.gray500 }}>Connexion avec email</div>
                        </div>
                      </Button>
                      <Button variant="outline" onClick={() => setStep('phone-input')} style={methodBtnStyle}>
                        <div style={methodIconStyle(C.accent)}>
                          <IconPhone style={{ width: '1.25rem', height: '1.25rem', color: C.white }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: C.gray900 }}>
                            Numéro de téléphone <span style={{ color: C.gray500 }}>(Sénégal)</span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: C.gray500 }}>Connexion avec code SMS</div>
                        </div>
                      </Button>
                      {/* Accès OTP temporairement masqué. On le réactivera dans une version ultérieure.
                      <Button variant="outline" onClick={() => setStep('access-otp')} style={methodBtnStyle}>
                        <div style={methodIconStyle(C.primary)}>
                          <IconKeyRound style={{ width: '1.25rem', height: '1.25rem', color: C.white }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: C.gray900 }}>Accès OTP</div>
                          <div style={{ fontSize: '0.75rem', color: C.gray500 }}>Disponible sur le backoffice IAM</div>
                        </div>
                      </Button>
                      */}
                      <div style={{ textAlign: 'center', fontSize: '0.875rem', paddingTop: '0.5rem' }}>
                        <span style={{ color: C.gray500 }}>Mot de passe oublié ? </span>
                        <button type="button" style={linkStyle} onClick={() => setShowPasswordRecovery(true)}>Récupérer</button>
                      </div>
                      <div style={{ position: 'relative', padding: '0.5rem 0' }}>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                          <span style={{ width: '100%', borderTop: `1px solid ${C.gray200}` }} />
                        </div>
                      </div>
                      {showSwitchToSignup && (
                        <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                          <span style={{ color: C.gray500 }}>Pas encore de compte ? </span>
                          <button type="button" style={linkStyle} onClick={onSwitchToSignup}>Inscrivez-vous</button>
                        </div>
                      )}
                      {showHomeLink && (
                        <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                          <button
                            type="button"
                            style={linkStyle}
                            onClick={() => { if (homeUrl) window.location.href = homeUrl; }}
                          >
                            Retourner à l'accueil
                          </button>
                        </div>
                      )}
                      <div style={{ textAlign: 'center', fontSize: '0.72rem', color: C.gray500, paddingTop: '0.25rem' }}>
                        OLLAID SSO v{SSO_VERSION} · © 2026 OLLAID
                      </div>
                    </div>
                  </DialogBody>
                </>
              )}

              {/* ===== EMAIL CHECK ===== */}
              {step === 'email-check' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <BrandLinkRow
                      icon={<IconMail style={{ width: '1.25rem', height: '1.25rem', color: C.accent }} />}
                      faviconSrc={packageFavicon}
                      faviconAlt="Ollaid SSO"
                      iconBg={C.accent + '1a'}
                      iconColor={C.accent}
                    />
                    <DialogTitle>Connexion par email</DialogTitle>
                    <DialogDescription>Entrez votre adresse email</DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {renderError()}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Label htmlFor="email">Adresse email</Label>
                        <Input id="email" type="email" placeholder="vous@exemple.com" value={email}
                          onChange={e => setEmail(e.target.value)} disabled={isSubmitting}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEmailCheck(); } }}
                        />
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <button type="button" style={{ ...linkStyle, fontSize: '0.75rem' }} onClick={() => setShowPasswordRecovery(true)}>
                          Mot de passe oublié ?
                        </button>
                      </div>
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    {renderLoaderBtn('Continuer', 'Vérification...', handleEmailCheck, !email.trim())}
                    {showSwitchToSignup && (
                      <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                        <span style={{ color: C.gray500 }}>Pas encore de compte ? </span>
                        <button type="button" style={linkStyle} onClick={onSwitchToSignup}>Inscrivez-vous</button>
                      </div>
                    )}
                  </DialogFooter>
                </>
              )}

              {/* ===== EMAIL PASSWORD ===== */}
              {step === 'email-password' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <BrandLinkRow
                      icon={<IconMail style={{ width: '1.25rem', height: '1.25rem', color: C.accent }} />}
                      faviconSrc={packageFavicon}
                      faviconAlt="Ollaid SSO"
                      iconBg={C.accent + '1a'}
                      iconColor={C.accent}
                    />
                    {userName && <DialogTitle>Bonjour {userName} 👋</DialogTitle>}
                    <DialogDescription>{email}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleEmailPasswordSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <DialogBody>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {renderError()}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Label htmlFor="password">Mot de passe</Label>
                            <button type="button" style={{ ...linkStyle, fontSize: '0.75rem' }} onClick={() => setShowPasswordRecovery(true)}>
                              Mot de passe oublié ?
                            </button>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password}
                              onChange={e => setPassword(e.target.value)} disabled={isSubmitting || passwordSubmitting}
                              style={{ paddingRight: '2.5rem' }}
                            />
                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                              style={{ position: 'absolute', right: 0, top: 0, height: '100%', padding: '0 0.75rem', background: 'none', border: 'none', cursor: 'pointer' }}>
                              {showPassword ? (
                                <IconEyeOff style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
                              ) : (
                                <IconEye style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </DialogBody>
                    <DialogFooter>
                      <Button type="submit" disabled={isSubmitting || passwordSubmitting || !password} style={{ width: '100%' }}>
                        {isSubmitting ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <IconLoader2 style={{ width: '1rem', height: '1rem' }} />
                            Connexion...
                          </span>
                        ) : 'Se connecter'}
                      </Button>
                    </DialogFooter>
                  </form>
                </>
              )}

              {/* ===== EMAIL OTP ===== */}
              {step === 'email-otp' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <BrandLinkRow
                      icon={<IconMail style={{ width: '1.25rem', height: '1.25rem', color: C.accent }} />}
                      faviconSrc={packageFavicon}
                      faviconAlt="Ollaid SSO"
                      iconBg={C.accent + '1a'}
                      iconColor={C.accent}
                    />
                    <DialogTitle>Vérification</DialogTitle>
                    <DialogDescription>
                      Un code de vérification a été envoyé {getOtpDeliveryLabel(otpMethod, otpSentTo, 'email')}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {renderError()}
                      <OTPInput value={emailOtpCode} onChange={setEmailOtpCode} disabled={isSubmitting} />
                      {renderResendLink()}
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    {renderLoaderBtn('Vérifier', 'Vérification...', handleEmailOtpVerify, emailOtpCode.length !== 6)}
                  </DialogFooter>
                </>
              )}

              {/* ===== PHONE INPUT ===== */}
              {step === 'phone-input' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <BrandLinkRow
                      icon={<IconPhone style={{ width: '1.25rem', height: '1.25rem', color: C.accent }} />}
                      faviconSrc={packageFavicon}
                      faviconAlt="Ollaid SSO"
                      iconBg={C.accent + '1a'}
                      iconColor={C.accent}
                    />
                    <DialogTitle>Connexion par téléphone</DialogTitle>
                    <DialogDescription>Entrez votre numéro pour recevoir un code de vérification</DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {renderError()}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Label>Indicatif</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', backgroundColor: C.gray100, borderRadius: '0.375rem' }}>
                          <span style={{ fontSize: '1.125rem' }}>🇸🇳</span>
                          <span style={{ fontWeight: 500, color: C.gray900 }}>Sénégal (+221)</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Label htmlFor="login-phone">Numéro de téléphone</Label>
                        <Input id="login-phone" type="tel" placeholder="77 123 45 67" value={phone}
                          onChange={e => { const val = e.target.value.replace(/\D/g, '').slice(0, 9); setPhone(val); }}
                          disabled={isSubmitting} maxLength={9}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePhoneInit(); } }}
                        />
                        <p style={{ fontSize: '0.75rem', color: C.gray500 }}>9 chiffres requis ({phone.replace(/\D/g, '').length}/9)</p>
                      </div>
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    {renderLoaderBtn('Recevoir le code', 'Envoi en cours...', handlePhoneInit, phone.replace(/\D/g, '').length !== 9)}
                    {showSwitchToSignup && (
                      <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                        <span style={{ color: C.gray500 }}>Pas encore de compte ? </span>
                        <button type="button" style={linkStyle} onClick={onSwitchToSignup}>Inscrivez-vous</button>
                      </div>
                    )}
                  </DialogFooter>
                </>
              )}

              {/* ===== PHONE OTP ===== */}
              {step === 'phone-otp' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <BrandLinkRow
                      icon={<IconPhone style={{ width: '1.25rem', height: '1.25rem', color: C.accent }} />}
                      faviconSrc={packageFavicon}
                      faviconAlt="Ollaid SSO"
                      iconBg={C.accent + '1a'}
                      iconColor={C.accent}
                    />
                    <DialogTitle>Vérification</DialogTitle>
                    <DialogDescription>
                      Un code de vérification a été envoyé {getOtpDeliveryLabel(otpMethod, otpSentTo, 'phone')}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {renderError()}
                      <OTPInput value={phoneOtpCode} onChange={setPhoneOtpCode} disabled={isSubmitting} />
                      {renderResendLink()}
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    {renderLoaderBtn('Se connecter', 'Vérification...', handlePhoneVerify, phoneOtpCode.length !== 6)}
                  </DialogFooter>
                </>
              )}

              {/* ===== ACCESS OTP ===== */}
              {step === 'access-otp' && (
                <>
                  <DialogHeader style={{ textAlign: 'center' }}>
                    <div style={iconCircle(C.primary)}>
                      <IconKeyRound style={{ width: '1.5rem', height: '1.5rem', color: C.white }} />
                    </div>
                    <DialogTitle>Code d'accès</DialogTitle>
                    <DialogDescription>Entrez le code d'accès à 8 chiffres</DialogDescription>
                  </DialogHeader>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {renderError()}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Label>Code d'accès (8 chiffres)</Label>
                        <Input type="text" inputMode="numeric" placeholder="0000-0000" value={accessOtpCode}
                          onChange={e => setAccessOtpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                          disabled={isSubmitting}
                          style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.15em', fontFamily: 'monospace' }}
                          maxLength={8}
                        />
                      </div>
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    {renderLoaderBtn('Se connecter', 'Vérification...', handleAccessOtpSubmit, accessOtpCode.length !== 8)}
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <PasswordRecoveryModal
        open={showPasswordRecovery}
        onOpenChange={setShowPasswordRecovery}
        onSuccess={() => setShowPasswordRecovery(false)}
        saasApiUrl={saasApiUrl}
        iamApiUrl={iamApiUrl}
        
      />
    </>
  );
}

export default LoginModal;
